import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TypsaOrgUnitDialog = ({ open, onOpenChange, unit, onSuccess }) => {
  const { currentUser, editorUsers } = useUser();
  const [formData, setFormData] = useState({
    category: 'division',
    value: '',
    owner: '',
    description: '',
    website: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (unit) {
      setFormData({
        category: unit.category || 'division',
        value: unit.value || '',
        owner: unit.owner || '',
        description: unit.description || '',
        website: unit.website || '',
        notes: unit.notes || ''
      });
    } else {
      setFormData({
        category: 'division',
        value: '',
        owner: '',
        description: '',
        website: '',
        notes: ''
      });
    }
  }, [unit, open]);

  const getCategoryLabel = (category) => {
    const labels = {
      'division': 'División',
      'departamento': 'Departamento',
      'territorio': 'Territorio',
      'grupo': 'Grupo'
    };
    return labels[category] || category;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.value.trim()) {
      toast.error('El valor es obligatorio');
      return;
    }

    setLoading(true);
    try {
      if (unit) {
        await axios.put(`${API}/typsa-org-units/${unit.id}`, formData, {
          headers: { 'x-user-email': currentUser?.email }
        });
        toast.success('Unidad organizativa actualizada');
      } else {
        await axios.post(`${API}/typsa-org-units`, formData, {
          headers: { 'x-user-email': currentUser?.email }
        });
        toast.success('Unidad organizativa creada');
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving TYPSA org unit:', error);
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="typsa-org-unit-dialog">
        <DialogHeader>
          <DialogTitle>
            {unit ? 'Editar' : 'Nueva'} Unidad Organizativa TYPSA
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="category">Categoría *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="typsa-unit-category-select">
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="division">División</SelectItem>
                  <SelectItem value="departamento">Departamento</SelectItem>
                  <SelectItem value="territorio">Territorio</SelectItem>
                  <SelectItem value="grupo">Grupo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="value">Valor / Nombre *</Label>
              <Input
                id="value"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder={`Ej: ${getCategoryLabel(formData.category)} Norte`}
                data-testid="typsa-unit-value-input"
                required
              />
            </div>

            <div>
              <Label htmlFor="owner">Responsable</Label>
              <Select
                value={formData.owner || "none"}
                onValueChange={(value) => setFormData({ ...formData, owner: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="typsa-unit-owner-select">
                  <SelectValue placeholder="Seleccionar responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {editorUsers?.map((user) => (
                    <SelectItem key={user.email} value={user.name}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="website">Sitio Web</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
                data-testid="typsa-unit-website-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional..."
                rows={2}
                data-testid="typsa-unit-description-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
                data-testid="typsa-unit-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="typsa-unit-save-btn">
              {loading ? 'Guardando...' : unit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TypsaOrgUnitDialog;
